import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo, useState} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {BenchlingExpression} from 'benchling-expressions/BenchlingExpressionTypes';
import {getDisplayNameFromFunctionSignature} from 'benchling-expressions/BenchlingFunctionSignatures';
import BenchlingConfig from 'BenchlingConfig';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ComplexTypeOperator} from 'data-types/benchling-type-def/ComplexTypes';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {getClientIdForProcessFlowchartEdge} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {ProcessGraphBuilderNode} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  getPartialConstant,
  getPartialExpression,
  getPartialLookup,
  NODE_DETAILS_TYPE_TO_ICON,
} from 'processes/modals/router-config/RouterConfigUtils';
import RouterRuleComponent from 'processes/modals/router-config/RouterRuleComponent';
import {Schema} from 'schemas/SchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_DARKER, GRAY_LIGHTER, RED} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {DeepPartial} from 'util/Types';

import {ProcessOutputSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessOutputSchemaGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {getAllowedOperatorAndValueInfo} from './getAllowedOperatorAndValueInfo';
import {
  CreateOrUpdateRouterFunctionSpec,
  CreateRouterFunctionSpec,
  sharedStyles,
  ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME,
} from './RouterConfigConstants';

interface ConditionalLogicConfigErrors {
  isNextNodeDuplicate: boolean;
}

export interface ConditionalLogicConfigProps {
  routerNodeClientId: string;
  flowchartClientId: string;
  onIsRouterFunctionNameBeingEdited: (isBeingEdited: boolean) => void;
  functionSpec: CreateOrUpdateRouterFunctionSpec & CreateRouterFunctionSpec;
  setFunctionSpec: (newFunctionSpec: CreateOrUpdateRouterFunctionSpec & CreateRouterFunctionSpec) => void;
  inputNode: ProcessGraphBuilderNode;
  flowchartTaskSchema: Schema;
  immediateChildNodes: Array<ProcessGraphBuilderNode>;
  inputSchema: ProcessTaskSchemaGraphQL | ProcessOutputSchemaGraphQL;
  didInitialFlowchartHaveChainedRouters: boolean;
  doesCurrentFlowchartHaveChainedRouters: boolean;
  analyticsMetadata: AnalyticsMetadataForContext;
  dataTestAttrs?: DataTestAttrs;
  errors: ConditionalLogicConfigErrors;
}

const NEXT_NODE_PLACEHOLDER_TEXT = 'select destination';

export default function ConditionalLogicConfig(props: ConditionalLogicConfigProps): JSX.Element {
  const [toNodeName, setToNodeName] = useState('');
  const [toNodeType, setToNodeType] = useState<ProcessFlowchartNodeDetailsType>();
  const [toClientId, setToClientId] = useState(props.functionSpec.edgeConfigReference?.toNodeClientId);

  const {expression, lookupConfig, operatorDisplayName, selectorId, allowedOperators, isMultiplex, typeDef} =
    useMemo(() => {
      const expression = getPartialExpression(
        props.functionSpec.expression as DeepPartial<BenchlingExpression>
      );

      if (BenchlingConfig.WORKFLOWS_ENABLE_AND_OR_CONDITIONS) {
        return {
          expression,
          lookupConfig: null,
          operatorDisplayName: null,
          selectorId: null,
          allowedOperators: null,
          isMultiplex: false,
          typeDef: null,
        };
      }

      const partialLookup = getPartialLookup(expression);
      const partialLookupConfig = partialLookup?.lookupConfig;
      const lookupConfig = partialLookupConfig ? (partialLookupConfig as LookupConfigSpec) : null;

      const operatorName = expression?.name;
      assert(
        operatorName == null || ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME[operatorName] != null,
        'Operator is not allowed',
        {operatorName}
      );

      const {
        isMultiplex,
        allowedOperators,
        selectorId = null,
        typeDef,
      } = lookupConfig
        ? getAllowedOperatorAndValueInfo(lookupConfig, props.inputSchema)
        : {isMultiplex: false, allowedOperators: null, selectorId: null, typeDef: null};
      const operatorDisplayName = operatorName
        ? getDisplayNameFromFunctionSignature(ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME[operatorName], typeDef)
        : null;

      return {
        expression,
        lookupConfig,
        operatorDisplayName,
        selectorId,
        allowedOperators,
        isMultiplex,
        typeDef,
      };
    }, [props.functionSpec.expression, props.inputSchema]);

  useMemo(() => {
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (props.functionSpec.edgeConfigReference === null) {
      // If the edge config reference is null, the destination node is the flowchart output.
      setToNodeName('Outputs');
      setToNodeType(undefined);
    } else if (toClientId == null) {
      setToNodeName(NEXT_NODE_PLACEHOLDER_TEXT);
      setToNodeType(undefined);
    } else {
      const initialToNode = props.immediateChildNodes.find((n) => n.clientId === toClientId);
      assertAndContinue(initialToNode != null, 'Next node is invalid');
      setToNodeName(initialToNode?.name ?? NEXT_NODE_PLACEHOLDER_TEXT);
      setToNodeType(initialToNode?.type);
    }
  }, [props.immediateChildNodes, props.functionSpec.edgeConfigReference, toClientId]);

  const getIsMismatchedTypeError = (): boolean => {
    if (expression?.name == null || allowedOperators == null) {
      return false;
    } else if (allowedOperators.find(({functionName}) => functionName === expression.name) == null) {
      return true;
    }
    const lookupExpression = getPartialLookup(expression);
    const rightHandExpression = getPartialConstant(expression);

    if (lookupExpression == null || rightHandExpression == null || typeDef == null) {
      return false;
    }
    const lookupItemType = isMultiplex
      ? {name: ComplexTypeOperator.LIST, ofType: typeDef, selectorId}
      : {name: typeDef, selectorId};
    const rightHandItemType = rightHandExpression.itemType;
    if (rightHandItemType == null) {
      return false;
    }

    if (typeDef === LeafType.DROPDOWN_OPTION && lookupItemType.selectorId !== rightHandItemType.selectorId) {
      return true;
    }

    const rightHandItemComplexBaseType =
      rightHandItemType.name === ComplexTypeOperator.LIST ? rightHandItemType.ofType : undefined;

    let complexItemBaseTypesMismatch;
    if (isMultiplex) {
      complexItemBaseTypesMismatch = lookupItemType.ofType !== rightHandItemComplexBaseType;
    } else {
      complexItemBaseTypesMismatch = rightHandItemComplexBaseType !== undefined;
    }

    if (complexItemBaseTypesMismatch) {
      return true;
    }

    return false;
  };

  const isMismatchedTypesError = getIsMismatchedTypeError();

  return (
    <div
      className={classNames(
        css(styles.routeBlock),
        isMismatchedTypesError && !BenchlingConfig.WORKFLOWS_ENABLE_AND_OR_CONDITIONS && css(styles.withError)
      )}
      {...getTestProps(props.dataTestAttrs)}
    >
      <div className={css(styles.rowHeader)}>
        {isMismatchedTypesError && !BenchlingConfig.WORKFLOWS_ENABLE_AND_OR_CONDITIONS && (
          <div className={css(styles.errorIconWrapper)}>
            <BenchlingIcon icon="error" className={css(styles.errorIcon)} />
          </div>
        )}
        <div className={css(sharedStyles.routerOrRouteName)}>
          <EditableTextField
            value={props.functionSpec.name}
            onIsBeingEditedChanged={props.onIsRouterFunctionNameBeingEdited}
            requestChange={(newName) => {
              props.setFunctionSpec({...props.functionSpec, name: String(newName)});
            }}
            dataTestAttrs={{component: 'ConditionalLogicConfig', element: 'functionName'}}
          />
        </div>
      </div>
      {/* Eventually should be replaced by MultipleConditionConfig or null */}
      <RouterRuleComponent
        {...props}
        isMismatchedTypesError={isMismatchedTypesError}
        operatorDisplayName={operatorDisplayName}
        selectorId={selectorId}
        lookupConfig={lookupConfig}
        allowedOperators={allowedOperators}
        isMultiplex={isMultiplex}
        typeDef={typeDef}
      />
      <div className={css(sharedStyles.row)}>
        {props.functionSpec.isDefault ? 'Go to' : 'then go to'}
        <Tooltip text={toNodeName} placement={FloatingPlacement.TOP} isDisabled={toClientId == null}>
          <SelectDropdown
            className={css(styles.routerInputControl)}
            buttonClassName={classNames({'fieldInput-input--error': props.errors.isNextNodeDuplicate})}
            displayName={toNodeName}
            shouldEllipsize={true}
            icon={toNodeType ? NODE_DETAILS_TYPE_TO_ICON[toNodeType] : undefined}
            iconClassName={css(styles.iconColor)}
            menu={
              <SearchableMenu
                height={MenuHeight.MEDIUM}
                width={MenuWidth.FULL}
                scrollable={true}
                optionDatas={props.immediateChildNodes.map((node, index) => ({
                  name: node.name,
                  value: node,
                  dataTestAttrs: {
                    component: 'ConditionalLogicConfig',
                    element: 'nextNodeOptionData',
                    index: `${props.dataTestAttrs?.key}-${index}`,
                  },
                  icon: node.type ? NODE_DETAILS_TYPE_TO_ICON[node.type] : undefined,
                  iconClassName: css(styles.iconColor),
                }))}
                selectedValue={toNodeName}
                onSelect={(node: ProcessGraphBuilderNode) => {
                  setToClientId(node.clientId);
                  const baseEdgeReference = {
                    clientId: getClientIdForProcessFlowchartEdge(props.routerNodeClientId, node.clientId),
                    fromNodeClientId: props.routerNodeClientId,
                    toNodeClientId: node.clientId,
                  };

                  props.setFunctionSpec({
                    ...props.functionSpec,
                    edgeConfigReference: baseEdgeReference,
                  });
                }}
              />
            }
            dataTestAttrs={{component: 'ConditionalLogicConfig', element: 'toNodeDropdown'}}
          />
        </Tooltip>
      </div>

      {isMismatchedTypesError && !BenchlingConfig.WORKFLOWS_ENABLE_AND_OR_CONDITIONS && (
        <div
          className={css(styles.errorText)}
          {...getTestProps({component: 'ConditionalLogicConfig', element: 'mismatchedTypesErrorMessage'})}
        >
          The lookup and constant value are not compatible with the{' '}
          {expression?.name != null
            ? `'${getDisplayNameFromFunctionSignature(
                ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME[expression.name],
                typeDef
              )}'`
            : 'provided'}{' '}
          operator.
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  routeBlock: {
    backgroundColor: GRAY_LIGHTER,
    margin: 16,
    marginTop: 0,
    paddingBlock: 8,
    paddingInline: 16,
  },
  rowHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  withError: {
    border: `1px solid ${RED}`,
  },
  errorIconWrapper: {
    marginRight: 8,
  },
  errorIcon: {
    color: RED,
  },
  errorText: {
    color: RED,
  },
  iconColor: {
    color: GRAY_DARKER,
  },
  routerInputControl: {
    width: 278,
  },
});
